import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import SettingsSidebar, { SettingsTab } from './components/SettingsSidebar';
import ProfileSection from './components/ProfileSection';
import PreferencesSection from './components/PreferencesSection';
import LocationLanguageSection from './components/LocationLanguageSection';
import InterestsSection from './components/InterestsSection';
import PrivacySection from './components/PrivacySection';
import NotificationsSection from './components/NotificationsSection';
import AccountSection from './components/AccountSection';

const tabTitles: Record<SettingsTab, { title: string; icon: string }> = {
  profile: { title: 'Profilim', icon: 'ri-user-line' },
  preferences: { title: 'Tercihler', icon: 'ri-equalizer-line' },
  location: { title: 'Konum & Dil', icon: 'ri-map-pin-line' },
  interests: { title: 'İlgi Alanları', icon: 'ri-heart-3-line' },
  privacy: { title: 'Gizlilik & Güvenlik', icon: 'ri-shield-line' },
  notifications: { title: 'Bildirimler', icon: 'ri-notification-3-line' },
  account: { title: 'Hesap', icon: 'ri-settings-3-line' },
};

const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');

  const renderContent = () => {
    switch (activeTab) {
      case 'profile': return <ProfileSection />;
      case 'preferences': return <PreferencesSection />;
      case 'location': return <LocationLanguageSection />;
      case 'interests': return <InterestsSection />;
      case 'privacy': return <PrivacySection />;
      case 'notifications': return <NotificationsSection />;
      case 'account': return <AccountSection />;
      default: return <ProfileSection />;
    }
  };

  const { title, icon } = tabTitles[activeTab];

  return (
    <div className="min-h-screen bg-[#05070d]">
      <Header />

      {/* BG subtle orbs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 right-1/4 w-[400px] h-[400px] rounded-full bg-violet-600/5 blur-[100px]" />
        <div className="absolute bottom-1/3 left-1/4 w-[300px] h-[300px] rounded-full bg-cyan-500/5 blur-[80px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-28 md:pt-32 pb-20">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-white/30 text-xs mb-8">
          <Link to="/" className="hover:text-white/60 transition-colors cursor-pointer">Ana Sayfa</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-white/55">Ayarlar</span>
          <i className="ri-arrow-right-s-line" />
          <span className="text-cyan-400/80">{title}</span>
        </div>

        {/* Page heading */}
        <div className="flex items-center gap-4 mb-10">
          <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-500/20 to-violet-600/20 border border-white/10">
            <i className={`${icon} text-cyan-400 text-xl`} />
          </div>
          <div>
            <h1 className="text-white font-extrabold text-2xl md:text-3xl">Ayarlar</h1>
            <p className="text-white/35 text-sm">Hesap ve tercihlerini yönet</p>
          </div>
        </div>

        {/* Main layout */}
        <div className="flex flex-col lg:flex-row gap-6 items-start">
          {/* Sidebar */}
          <SettingsSidebar active={activeTab} onTabChange={setActiveTab} />

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="bg-[#0d1117]/60 backdrop-blur-xl rounded-3xl border border-white/8 p-6 md:p-8">
              {renderContent()}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SettingsPage;
